import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  CircularProgress, Typography, Button,
} from '@mui/material';
import { getAllProgramReportGroupDetail } from '../../../../Services/app/oeeservice';
import { getMasterList, saveMasterListSafely } from '../../../../Services/app/masterservice';

const BORDER = '#ECEFF3';
const JOB_COST_KEY = 'insightsJobCostList';

// machine_name + job_name + cycle_time identifies one cost entry.
const jobCostKey = (row) => `${row.machine_name}|${row.job_name}|${row.cycle_time}`;

export default function JobCostSettings({ customerId, filters }) {
  const [combos, setCombos] = useState([]);
  const [savedCosts, setSavedCosts] = useState({});
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [bulkValue, setBulkValue] = useState('45');
  const [search, setSearch] = useState('');

  const loadAll = useCallback(async () => {
    if (!customerId || !filters) { setCombos([]); return; }
    setLoading(true);
    setError('');
    setSaveMessage('');
    try {
      const [reportRows, costList] = await Promise.all([
        getAllProgramReportGroupDetail(filters.machines, filters.startTimeMs, filters.endTimeMs),
        getMasterList(customerId, JOB_COST_KEY),
      ]);
      const rows = Array.isArray(reportRows) ? reportRows : (reportRows?.data || []);
      const seen = new Set();
      const unique = [];
      rows.forEach((r) => {
        const key = jobCostKey(r);
        if (!seen.has(key)) {
          seen.add(key);
          unique.push({ machine_name: r.machine_name, job_name: r.job_name, cycle_time: r.cycle_time });
        }
      });
      unique.sort((a, b) => a.machine_name.localeCompare(b.machine_name) || a.job_name.localeCompare(b.job_name));
      setCombos(unique);

      const costMap = {};
      costList.forEach((e) => { costMap[jobCostKey(e)] = e.cost_per_part; });
      setSavedCosts(costMap);
      const initialInputs = {};
      unique.forEach((row) => {
        const key = jobCostKey(row);
        if (costMap[key] !== undefined && costMap[key] !== null) initialInputs[key] = String(costMap[key]);
      });
      setInputs(initialInputs);
    } catch (err) {
      console.error('Failed to load job cost settings', err);
      setError('Failed to load machine/job list.');
    } finally {
      setLoading(false);
    }
  }, [customerId, filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleChange = (row, value) => {
    setInputs((prev) => ({ ...prev, [jobCostKey(row)]: value }));
  };

  const handleSave = async (effectiveInputs) => {
    const useInputs = effectiveInputs || inputs;
    setSaving(true);
    setSaveMessage('');
    try {
      const changed = combos.filter((row) => {
        const key = jobCostKey(row);
        const raw = useInputs[key];
        const nextVal = raw === undefined || raw === '' ? null : Number(raw);
        const prevVal = savedCosts[key] ?? null;
        return nextVal !== prevVal && !(nextVal === null && prevVal === null) && !Number.isNaN(nextVal);
      });
      if (changed.length === 0) {
        setSaveMessage('Nothing to save.');
        return;
      }
      const hasDeletion = changed.some((row) => {
        const key = jobCostKey(row);
        const raw = useInputs[key];
        const nextVal = raw === undefined || raw === '' ? null : Number(raw);
        return nextVal === null && savedCosts[key] !== undefined && savedCosts[key] !== null;
      });
      await saveMasterListSafely({
        customerId,
        key: JOB_COST_KEY,
        allowShrink: hasDeletion,
        baselineCount: Object.keys(savedCosts).length,
        mutate: (freshList) => {
          let next = freshList.slice();
          changed.forEach((row) => {
            const key = jobCostKey(row);
            const raw = useInputs[key];
            const nextVal = raw === undefined || raw === '' ? null : Number(raw);
            const idx = next.findIndex(
              (e) => e.machine_name === row.machine_name
                && e.job_name === row.job_name
                && e.cycle_time === row.cycle_time
            );
            if (nextVal === null) {
              if (idx !== -1) next.splice(idx, 1);
              return;
            }
            const entry = {
              machine_name: row.machine_name,
              job_name: row.job_name,
              cycle_time: row.cycle_time,
              cost_per_part: nextVal,
            };
            if (idx === -1) next.push(entry);
            else next[idx] = entry;
          });
          return next;
        },
      });
      setSaveMessage(`Saved ${changed.length} cost${changed.length > 1 ? 's' : ''}.`);
      await loadAll();
    } catch (err) {
      console.error('Failed to save job costs', err);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToAllAndSave = async () => {
    if (bulkValue === '' || Number.isNaN(Number(bulkValue))) return;
    const next = {};
    combos.forEach((row) => { next[jobCostKey(row)] = bulkValue; });
    setInputs(next);
    await handleSave(next);
  };

  if (!filters) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select filters and click Submit on the Insights page, then reopen Settings to configure per-job costs.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) {
    return <Typography variant="body2" color="error">{error}</Typography>;
  }

  if (combos.length === 0) {
    return <Typography variant="body2" color="text.secondary">No jobs found for the current filters.</Typography>;
  }

  const searchTerm = search.trim().toLowerCase();
  const filteredCombos = searchTerm
    ? combos.filter((row) =>
        row.machine_name.toLowerCase().includes(searchTerm)
        || row.job_name.toLowerCase().includes(searchTerm)
      )
    : combos;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search machine / job"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
        <TextField
          size="small"
          type="number"
          label="Set all to"
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
          sx={{ width: 140 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={handleApplyToAllAndSave}
          disabled={saving || bulkValue === '' || Number.isNaN(Number(bulkValue))}
        >
          {saving ? 'Saving...' : `Apply to all ${combos.length} & Save`}
        </Button>
      </Box>
      <Box sx={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${BORDER}`, borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }}>Machine</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }}>Job</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }}>Cycle Time</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }} align="right">Cost / Part</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCombos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ fontSize: 12, color: '#898781', py: 2 }}>
                  No matches for "{search}".
                </TableCell>
              </TableRow>
            ) : filteredCombos.map((row) => {
              const key = jobCostKey(row);
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontSize: 12 }}>{row.machine_name}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{row.job_name}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{row.cycle_time}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="0.00"
                      value={inputs[key] ?? ''}
                      onChange={(e) => handleChange(row, e.target.value)}
                      onBlur={() => handleSave()}
                      inputProps={{ style: { textAlign: 'right', fontSize: 12, padding: '4px 6px' } }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 1.5 }}>
        {saveMessage && (
          <Typography variant="caption" color="text.secondary">{saveMessage}</Typography>
        )}
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Job Costs'}
        </Button>
      </Box>
    </Box>
  );
}
